import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { API } from "../hooks/useAuth";

const TIERS = [
  {
    key: "free",
    name: "Free",
    price: 0,
    period: "forever",
    description: "Explore the platform",
    color: "var(--subtext)",
    features: [
      "1 region",
      "Air quality data only",
      "7-day history",
      "Basic dashboard",
      "No API access",
    ],
    cta: "Get Started",
    highlight: false,
  },
  {
    key: "basic",
    name: "Basic",
    price: 49,
    period: "/ month",
    description: "For small teams & analysts",
    color: "#FFB800",
    features: [
      "3 regions",
      "Air quality + Weather",
      "30-day history",
      "API access (1,000 calls/day)",
      "CSV data export",
      "Email alerts",
    ],
    cta: "Start Basic",
    highlight: false,
  },
  {
    key: "pro",
    name: "Pro",
    price: 149,
    period: "/ month",
    description: "Full intelligence platform",
    color: "var(--info)",
    features: [
      "Unlimited regions",
      "All data types incl. GIS + Fires",
      "365-day history",
      "API access (10,000 calls/day)",
      "AI anomaly detection",
      "Predictive alerts",
      "Natural language querying",
      "AI-generated reports",
    ],
    cta: "Start Pro",
    highlight: true,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: null,
    period: "custom",
    description: "For organisations at scale",
    color: "var(--accent)",
    features: [
      "Everything in Pro",
      "White-label dashboard",
      "Custom domain",
      "Unlimited API calls",
      "Unlimited history",
      "Custom data integrations",
      "Dedicated support",
      "SLA guarantee",
    ],
    cta: "Contact Sales",
    highlight: false,
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (tier) => {
    if (!user) { navigate("/login?next=pricing"); return; }
    if (tier === "enterprise") {
      window.location.href = "mailto:sales@highhorse.io?subject=Enterprise Enquiry";
      return;
    }
    if (tier === "free") { navigate("/dashboard"); return; }
    setLoading(tier);
    try {
      const r = await API.post(`/api/subscriptions/checkout/${tier}`);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", padding: "60px 24px" }}>
      {/* Header */}
      <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto 60px" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "4px 14px", borderRadius: 100,
          background: "rgba(0,229,170,0.08)", border: "1px solid rgba(0,229,170,0.2)",
          fontSize: 11, color: "var(--accent)", fontFamily: "var(--mono)",
          marginBottom: 20, letterSpacing: "0.1em"
        }}>
          INTELLIGENCE PLATFORM
        </div>
        <h1 style={{ fontFamily: "var(--sans)", fontSize: 42, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 16 }}>
          Environmental data that<br />
          <span style={{ color: "var(--accent)" }}>powers decisions</span>
        </h1>
        <p style={{ color: "var(--subtext)", fontSize: 15, lineHeight: 1.7 }}>
          From a free preview to enterprise white-label. Choose the plan that fits your organisation.
        </p>
      </div>

      {/* Tier cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 16, maxWidth: 1100, margin: "0 auto"
      }}>
        {TIERS.map(tier => (
          <div key={tier.key} className="card fade-up" style={{
            position: "relative",
            border: tier.highlight ? `1px solid ${tier.color}` : "1px solid var(--border)",
            boxShadow: tier.highlight ? `0 0 40px rgba(77,166,255,0.1)` : "none",
          }}>
            {tier.highlight && (
              <div style={{
                position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                background: tier.color, color: "#060910",
                padding: "3px 14px", borderRadius: 100,
                fontSize: 10, fontFamily: "var(--mono)", fontWeight: 700,
                letterSpacing: "0.1em", whiteSpace: "nowrap"
              }}>
                MOST POPULAR
              </div>
            )}

            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: tier.color, letterSpacing: "0.1em", marginBottom: 6 }}>
                {tier.name.toUpperCase()}
              </div>
              <div style={{ marginBottom: 4 }}>
                {tier.price !== null ? (
                  <>
                    <span style={{ fontSize: 36, fontWeight: 800, fontFamily: "var(--mono)", color: "var(--text)" }}>
                      ${tier.price}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--subtext)", marginLeft: 4 }}>{tier.period}</span>
                  </>
                ) : (
                  <span style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--mono)", color: "var(--text)" }}>
                    Custom
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--subtext)" }}>{tier.description}</div>
            </div>

            {/* Features */}
            <div style={{ marginBottom: 24 }}>
              {tier.features.map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 0", fontSize: 12, color: "var(--text)" }}>
                  <span style={{ color: tier.color, flexShrink: 0, marginTop: 1 }}>✓</span>
                  {f}
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              className="btn"
              onClick={() => handleUpgrade(tier.key)}
              disabled={loading === tier.key || (user?.tier === tier.key)}
              style={{
                width: "100%", justifyContent: "center",
                background: tier.highlight ? tier.color : "transparent",
                border: `1px solid ${tier.highlight ? tier.color : "var(--border2)"}`,
                color: tier.highlight ? "#060910" : "var(--text)",
                opacity: user?.tier === tier.key ? 0.5 : 1,
              }}
            >
              {user?.tier === tier.key ? "Current Plan" : loading === tier.key ? "Loading..." : tier.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div style={{ textAlign: "center", marginTop: 48, fontSize: 12, color: "var(--subtext)" }}>
        All plans include a 14-day free trial. Cancel anytime. Prices in USD.
      </div>
    </div>
  );
}
